// src/admin/AdminUserDetails.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import AdminPageWrapper from './components/AdminPageWrapper';

function AdminUserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detect if we're in admin or director context
  const isDirectorContext = location.pathname.startsWith('/director');
  const basePath = isDirectorContext ? '/director' : '/admin';

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', action: '', message: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch user details
  const fetchUser = async () => {
    try {
      const res = await api.get(API.adminUserDetails(id));
      const userData = res.data.user || res.data; // Handle both response formats
      setUser(userData);
    } catch (err) {
      console.error('❌ Failed to fetch user:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Show confirmation dialog
  const showConfirmDialog = (field, value) => {
    const isActive = field === 'isActive';
    const isContentCreator = field === 'isContentCreator';
    const isTrustedUser = field === 'isTrustedUser';
    const isVerified = field === 'isVerified';
    
    let action, message;
    if (isActive) {
      action = value ? 'activate' : 'deactivate';
    } else {
      action = value ? 'enable' : 'disable';
    }
    
    // Create full name from first and last name
    const fullName = `${user?.FirstName || ''} ${user?.LastName || ''}`.trim();
    const displayName = fullName || user?.UserName || 'this user';
    
    if (isActive) {
      message = `Are you sure you want to ${action} user "${displayName}"?`;
    } else if (isContentCreator) {
      message = `Are you sure you want to ${action} content creator status for "${displayName}"?`;
    } else if (isTrustedUser) {
      message = `Are you sure you want to ${action} trusted user status for "${displayName}"?`;
    } else if (isVerified) {
      message = `Are you sure you want to ${value ? 'verify' : 'unverify'} user "${displayName}"?`;
    }
    
    setConfirmDialog({
      open: true,
      type: field,
      action: value,
      message: message
    });
  };

  // Generic update function for flags
  const updateFlag = async (field, value) => {
    // Close dialog immediately
    setConfirmDialog({ open: false, type: '', action: '', message: '' });
    
    // Map field names to database property names
    const fieldMap = {
      'isActive': 'IsActive',
      'isContentCreator': 'IsContentCreator',
      'isTrustedUser': 'IsTrustedUser',
      'isVerified': 'IsVerified'
    };
    
    // Optimistic update - update UI immediately
    const previousValue = user[fieldMap[field]];
    setUser({
      ...user,
      [fieldMap[field]]: value,
    });
    
    try {
      const payload = {};
      if (field === 'isActive') {
        payload.isActive = value;
      } else if (field === 'isContentCreator') {
        payload.isContentCreator = value;
      } else if (field === 'isTrustedUser') {
        payload.isTrustedUser = value;
      } else if (field === 'isVerified') {
        payload.isVerified = value;
      }
      
      const res = await api.patch(API.adminUserFlags(id), payload);
      
      if (!res.data.success) {
        throw new Error(res.data.error || 'Update failed');
      }
      
      // Show success message
      let action;
      if (field === 'isActive') {
        action = value ? 'activated' : 'deactivated';
      } else if (field === 'isContentCreator') {
        action = value ? 'enabled' : 'disabled';
      } else if (field === 'isTrustedUser') {
        action = value ? 'marked as trusted' : 'removed trusted status';
      } else if (field === 'isVerified') {
        action = value ? 'verified' : 'unverified';
      }
      
      const fullName = `${user?.FirstName || ''} ${user?.LastName || ''}`.trim();
      const displayName = fullName || user?.UserName || 'User';
      
      setSuccessMessage(`✅ User "${displayName}" has been ${action} successfully!`);
      setShowSuccess(true);
    } catch (err) {
      console.error(`❌ [updateFlag] Failed to update ${field}:`, err);
      console.error(`❌ [updateFlag] Error response:`, err.response?.data);
      
      // Revert optimistic update on error
      setUser({
        ...user,
        [fieldMap[field]]: previousValue,
      });
      
      alert(`❌ Failed to update ${field}. ${err.response?.data?.error || err.message || 'See console for details.'}`);
    }
  };

  // Handle confirmation
  const handleConfirm = () => {
    updateFlag(confirmDialog.type, confirmDialog.action);
  };

  // Reset password handler
  const handleResetPassword = async () => {
    try {
      await api.patch(API.adminUserResetPassword(id), { newPassword });
      
      // Create full name from first and last name
      const fullName = `${user?.FirstName || ''} ${user?.LastName || ''}`.trim();
      const displayName = fullName || user?.UserName || 'User';
      
      setSuccessMessage(`✅ Password reset successfully for user "${displayName}"!`);
      setShowSuccess(true);
      setNewPassword('');
      setOpenDialog(false);
    } catch (err) {
      console.error('❌ Failed to reset password:', err);
      alert('❌ Failed to reset password. See console for details.');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!user) {
    return <p>User not found.</p>;
  }

  return (
    <AdminPageWrapper 
      title="User Details" 
      subtitle={`Manage user account for ID: ${id}`}
    >
      {/* Success Notification */}
      {showSuccess && (
        <Alert
          severity="success"
          onClose={() => setShowSuccess(false)}
          sx={{ mb: 2 }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Back button */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
            variant="outlined"
            onClick={() => navigate(`${basePath}/users`, { state: { message: 'User updated successfully' } })}
        >
            ← Back to Users List
        </Button>
        </Stack>


      {/* User details */}
      <Typography variant="h5" gutterBottom>
        User Details – {user.UserName}
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
  <p><strong>UserID:</strong> {user.UserID}</p>
  <p><strong>Username:</strong> {user.UserName}</p>
  <p><strong>First Name:</strong> {user.FirstName || '—'}</p>
  <p><strong>Last Name:</strong> {user.LastName || '—'}</p>
  <p><strong>Email:</strong> {user.Email}</p>
  <p><strong>Mobile:</strong> {user.MobileNumber || '—'}</p>
  <p><strong>Role:</strong> {user.UserTypeName}</p>
  <p><strong>Referral Code:</strong> {user.ReferralCode}</p>
  <p><strong>Referred By:</strong> {user.ReferredByCode || '—'}</p>
  <p><strong>Status:</strong> {user.IsActive ? '✅ Active' : '❌ Inactive'}</p>
  <p><strong>Content Creator:</strong> {user.IsContentCreator ? '🎬 Yes' : 'No'}</p>
  <p><strong>Trusted User:</strong> {user.IsTrustedUser ? '✅ Yes' : 'No'}</p>
  <p><strong>Verified:</strong> {user.IsVerified ? '✅ Yes' : 'No'}</p>
  <p><strong>Joined:</strong> {new Date(user.CreatedAt).toLocaleString()}</p>
</Paper>


      {/* Actions */}
      <Stack direction="row" spacing={2}>
        {!isDirectorContext && (
          <Button
            variant="contained"
            color={user.IsActive ? 'error' : 'success'}
            onClick={() => showConfirmDialog('isActive', !user.IsActive)}
          >
            {user.IsActive ? 'Deactivate' : 'Activate'} User
          </Button>
        )}

        {!isDirectorContext && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpenDialog(true)}
          >
            Reset Password
          </Button>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={!!user.IsContentCreator}
              onChange={(e) =>
                showConfirmDialog('isContentCreator', e.target.checked)
              }
              color="primary"
            />
          }
          label="Content Creator"
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!user.IsTrustedUser}
              onChange={(e) =>
                showConfirmDialog('isTrustedUser', e.target.checked)
              }
              color="primary"
            />
          }
          label="Trusted User"
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!user.IsVerified}
              onChange={(e) =>
                showConfirmDialog('isVerified', e.target.checked)
              }
              color="primary"
            />
          }
          label="Verify"
        />
      </Stack>

      {/* Reset Password Dialog - Hidden for directors */}
      {!isDirectorContext && (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <TextField
              label="New Password"
              type="password"
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              color="primary"
              disabled={!newPassword}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: '', action: '', message: '' })}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', action: '', message: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminUserDetails;
